/**
 * Link (navigation) contract between an editor adapter and the editor (the Monaco based CodeEditor of the web
 * application, or another host such as the VS Code extension).
 *
 * Adapters describe clickable places of the document with plain objects and never touch the editor API; the
 * editor underlines the ranges and runs the target's open() on Ctrl+click. Links are computed once per content
 * change over the whole document, so provide() should stay cheap (text scanning) and any lookup belongs to open().
 *
 * The links deliberately have no keyboard shortcut: Monaco's "Go to Definition" would take over F12 (browser
 * dev tools) in every editor of the language, links leave the keyboard alone.
 */

/** 1-based range in the document, the end column is exclusive (Monaco IRange). */
export type CodeRange = {
	startLineNumber: number,
	startColumn: number,
	endLineNumber: number,
	endColumn: number,
};

export type CodeLinkTarget = {
	range: CodeRange,
	/** shown on hover as "{tooltip} (ctrl + click)"; Monaco shows "Follow link" without it */
	tooltip?: string,
	/** Ctrl+click on the range; the adapter opens whatever the link points to and reports its own errors */
	open(): void | Promise<void>,
};

export type CodeLinkProvider = {
	provide(content: string): CodeLinkTarget[] | Promise<CodeLinkTarget[]>,
};

/** Concatenates the targets of several providers. */
export function mergeLinkProviders(...providers: (CodeLinkProvider | null | undefined)[]): CodeLinkProvider | null {
	const list = providers.filter((provider): provider is CodeLinkProvider => !!provider);
	if (list.length <= 1) return list[0] ?? null;
	return {
		provide: async content => (await Promise.all(list.map(provider => provider.provide(content)))).flat(),
	};
}

/**
 * Range of the text between two 0-based offsets of the content (end exclusive), as 1-based line and column
 * numbers. Lines are separated by '\n'; a '\r' before it counts as a column of its line, like Monaco does.
 */
export function createRange(content: string, start: number, end: number): CodeRange {
	const position = (offset: number) => {
		const lineStart = content.lastIndexOf('\n', offset - 1) + 1;
		let lineNumber = 1;
		for (let i = content.indexOf('\n'); i >= 0 && i < lineStart; i = content.indexOf('\n', i + 1)) lineNumber++;
		return { lineNumber, column: offset - lineStart + 1 };
	};
	const from = position(start), to = position(end);
	return {
		startLineNumber: from.lineNumber,
		startColumn: from.column,
		endLineNumber: to.lineNumber,
		endColumn: to.column,
	};
}
